import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import "../../assets/css/faq.css";

const sections = [
  {
    id: "what-is-zoomey",
    num: "01",
    icon: "bi-robot",
    heading: "What is Zoomey?",
    faqs: [
      {
        q: "What is Zoomey?",
        a: "Zoomey is an AI-powered personal assistant built for freelancers, entrepreneurs, and busy professionals. It helps you manage tasks, notes, calendar, email, groceries, meetings, and more — all through natural conversation.",
      },
      {
        q: "Who is Zoomey for?",
        a: "Zoomey is designed for anyone who juggles multiple responsibilities — freelancers managing client work, entrepreneurs running a business, or professionals who want to offload mental overhead and stay organized effortlessly.",
      },
      {
        q: "How does Zoomey work?",
        a: "Simply chat with Zoomey like you would with a real assistant. Tell it what you need — create a task, set a reminder, draft an email, manage your grocery list — and Zoomey handles it instantly.",
      },
    ],
  },
  {
    id: "features",
    num: "02",
    icon: "bi-stars",
    heading: "Key Features",
    faqs: [
      {
        q: "What can Zoomey do?",
        a: "Zoomey offers 20+ built-in tools including task management, notes, calendar integration, email (Gmail), grocery lists, meeting management, reminders, file storage, web search, deep research, and a personal memory system.",
      },
      {
        q: "Does Zoomey support voice interaction?",
        a: "Yes! Zoomey supports real-time voice conversations powered by Gemini Live and OpenAI Realtime API. You can talk to Zoomey hands-free, and it can even see your screen or webcam during voice calls.",
      },
      {
        q: "Can Zoomey integrate with my existing tools?",
        a: "Absolutely. Zoomey connects with Google Calendar, Gmail, Google Drive, Google Docs, Google Sheets, and more through secure OAuth integrations. We're continuously adding new integrations.",
      },
      {
        q: "What is the Second Brain feature?",
        a: "Second Brain is Zoomey's personal knowledge management system based on the PARA method (Projects, Areas, Resources, Archives). It helps you organize and recall information across all your conversations and data.",
      },
    ],
  },
  {
    id: "pricing-billing",
    num: "03",
    icon: "bi-credit-card",
    heading: "Pricing & Billing",
    faqs: [
      {
        q: "Is Zoomey free to use?",
        a: "Zoomey offers a free tier with limited credits. For heavier usage, we offer affordable paid plans with more credits per month. Check our Pricing page for current plan details.",
      },
      {
        q: "How does the credit system work?",
        a: "Every AI interaction uses credits. Simple tasks use fewer credits, while complex research or voice calls use more. Your credit balance resets each billing cycle based on your plan.",
      },
      {
        q: "Can I upgrade or downgrade my plan?",
        a: "Yes, you can change your plan at any time from the Billing section. Upgrades take effect immediately, while downgrades apply at the end of your current billing period.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit cards, debit cards, and other payment methods supported by Stripe, our payment processor.",
      },
    ],
  },
  {
    id: "privacy-security",
    num: "04",
    icon: "bi-shield-lock",
    heading: "Data Privacy & Security",
    faqs: [
      {
        q: "Is my data safe with Zoomey?",
        a: "We use industry-standard encryption, secure database practices, and Row Level Security (RLS) to protect your data. We share your data only with the service providers needed to run Zoomey — including our third-party AI providers (Anthropic, OpenAI, and Google) — as described in our Privacy Policy.",
      },
      {
        q: "Does Zoomey store my conversations?",
        a: "Yes, your chat history is stored securely to provide context-aware responses and allow you to reference past conversations. You can delete your data at any time.",
      },
      {
        q: "Can Zoomey read my emails or calendar?",
        a: "Only if you explicitly connect those services through OAuth. Zoomey requests the minimum permissions needed, and you can disconnect integrations at any time from your settings.",
      },
    ],
  },
  {
    id: "account",
    num: "05",
    icon: "bi-person-gear",
    heading: "Account Management",
    faqs: [
      {
        q: "How do I create an account?",
        a: "You can sign up using your email or continue with Google. After signing in, Zoomey will guide you through a quick onboarding to personalize your experience.",
      },
      {
        q: "How do I reset my password?",
        a: "Click 'Forgot Password?' on the login page. We'll send a password reset link to your registered email address.",
      },
      {
        q: "Can I delete my account?",
        a: "Yes. You can request account deletion by contacting us at support@playces.in. We will remove all your data from our systems.",
      },
    ],
  },
  {
    id: "ai-capabilities",
    num: "06",
    icon: "bi-cpu",
    heading: "AI Capabilities",
    faqs: [
      {
        q: "Which AI models does Zoomey use?",
        a: "Zoomey uses multiple leading AI models including Claude (Anthropic), GPT (OpenAI), and Gemini (Google). The system automatically selects the best model based on your query complexity.",
      },
      {
        q: "How accurate are Zoomey's responses?",
        a: "Zoomey uses state-of-the-art AI models and provides highly accurate responses for most tasks. However, like all AI systems, responses should be verified for critical decisions. See our Terms for the full AI disclaimer.",
      },
      {
        q: "Can Zoomey learn my preferences?",
        a: "Yes! Zoomey has a built-in memory system that learns your preferences, habits, and frequently used information over time to provide more personalized assistance.",
      },
    ],
  },
  {
    id: "support",
    num: "07",
    icon: "bi-headset",
    heading: "Support & Contact",
    faqs: [
      {
        q: "How can I get help?",
        a: "You can email us at support@playces.in for any issues or questions. We aim to respond within 24 hours.",
      },
      {
        q: "Is there a mobile app?",
        a: "Mobile apps for iOS and Android are coming soon! In the meantime, Zoomey works great in your mobile browser.",
      },
      {
        q: "How do I report a bug?",
        a: "Please email bugs or feedback to support@playces.in with details about what happened and steps to reproduce the issue. Screenshots are always helpful!",
      },
    ],
  },
];

const FAQ = () => {
  const [activeId, setActiveId] = useState(sections[0].id);
  const [openItems, setOpenItems] = useState({});
  const scrollRef = useRef(null);
  const isDarkMode = Cookies.get("isDarkMode") === "true";

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { root: container, rootMargin: "-10% 0px -40% 0px", threshold: 0 }
    );
    sections.forEach(({ id }) => {
      const el = container.querySelector(`#${id}`);
      if (el) observer.observe(el);
    });

    // Activate last section when scrolled to bottom
    const lastId = sections[sections.length - 1].id;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 40) {
        setActiveId(lastId);
      }
    };
    container.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      container.removeEventListener("scroll", onScroll);
    };
  }, []);

  const scrollTo = (id) => {
    const container = scrollRef.current;
    const el = container?.querySelector(`#${id}`);
    if (!el || !container) return;
    container.scrollTo({ top: el.offsetTop - 24, behavior: "smooth" });
  };

  const toggleFaq = (key) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="faq-page">

      {/* Top Nav */}
      <nav className="faq-topbar">
        <div className="faq-topbar-inner">
          <Link to="/login" className="zoomey-logo-wrapper">
            <div className={`zoomey-logo-mark ${isDarkMode ? "dark" : "light"}`}>
              <div className="logo-glow"></div>
              <div className="logo-inner">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 7H22L8 21H22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="z-path" />
                  <circle cx="22" cy="7" r="2" fill="currentColor" className="z-dot" />
                </svg>
              </div>
            </div>
            <div className="zoomey-brand-text">
              <span className={`brand-name ${isDarkMode ? "dark" : "light"}`}>ZOOMEY</span>
            </div>
          </Link>
          <div className="faq-topbar-right">
            <Link to="/pricing" className="faq-topbar-link">Pricing</Link>
            <Link to="/terms-and-conditions" className="faq-topbar-link">Terms</Link>
            <Link to="/privacy-policy" className="faq-topbar-link">Privacy</Link>
          </div>
        </div>
      </nav>

      {/* Main Layout */}
      <div className="faq-layout">

        {/* Sidebar */}
        <aside className="faq-sidebar">
          <p className="faq-sidebar-label">Categories</p>
          <nav className="faq-sidebar-nav">
            {sections.map(({ id, num, heading }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`faq-nav-item ${activeId === id ? "active" : ""}`}
              >
                <span className="faq-nav-num">{num}</span>
                <span className="faq-nav-text">{heading}</span>
                {activeId === id && <span className="faq-nav-dot"></span>}
              </button>
            ))}
          </nav>

          <div className="faq-sidebar-contact">
            <p className="faq-sidebar-contact-label">Still have questions?</p>
            <a href="mailto:support@playces.in" className="faq-sidebar-contact-link">
              <i className="bi bi-envelope"></i>
              support@playces.in
            </a>
          </div>
        </aside>

        {/* Scrollable Content */}
        <div className="faq-right-scroll" ref={scrollRef}>

          {/* Hero */}
          <header className="faq-hero">
            <div className="faq-hero-bg-grid"></div>
            <div className="faq-hero-inner">
              <div className="faq-hero-badge">
                <i className="bi bi-question-circle"></i>
                Help Center
              </div>
              <h1 className="faq-hero-title">Frequently Asked Questions</h1>
              <p className="faq-hero-sub">
                Everything you need to know about Zoomey. Can't find what you're
                looking for? Reach out to our support team.
              </p>
            </div>
          </header>

          {/* Content */}
          <main className="faq-content">
            {sections.map(({ id, num, icon, heading, faqs }) => (
              <section key={id} id={id} className="faq-section">
                <div className="faq-section-header">
                  <div className="faq-section-num">{num}</div>
                  <div className="faq-section-title-wrap">
                    <div className="faq-section-icon-box">
                      <i className={`bi ${icon}`}></i>
                    </div>
                    <h2 className="faq-section-title">{heading}</h2>
                  </div>
                </div>
                <div className="faq-items">
                  {faqs.map((faq, i) => {
                    const key = `${id}-${i}`;
                    const isOpen = openItems[key];
                    return (
                      <div key={key} className={`faq-item ${isOpen ? "open" : ""}`}>
                        <button
                          className="faq-item-toggle"
                          onClick={() => toggleFaq(key)}
                          aria-expanded={isOpen}
                        >
                          <span className="faq-item-q">{faq.q}</span>
                          <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                        </button>
                        {isOpen && (
                          <div className="faq-item-a">
                            <p>{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </main>

          {/* Footer */}
          <footer className="faq-footer">
            <div className="faq-footer-inner">
              <div className="faq-footer-left">
                <span className="faq-footer-brand">ZOOMEY</span>
                <span>&copy; 2026 Playces.in. All rights reserved.</span>
              </div>
              <div className="faq-footer-links">
                <Link to="/pricing">Pricing</Link>
                <Link to="/faq">FAQ</Link>
                <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
                <Link to="/privacy-policy">Privacy Policy</Link>
                <Link to="/login">Sign In</Link>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
